import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { BookOpen, Shield, ChevronRight, Zap, Bug, Lock } from 'lucide-react';
import { LEARN_ARTICLES, SEVERITY_ORDER } from '../data/learnArticles';

const SITE = 'https://githubmate.ai';

const SEV_CLASS: Record<string, string> = {
  critical: 'sev-critical',
  high:     'sev-high',
  medium:   'sev-medium',
};

const SOURCE_ICONS: Record<string, React.ReactNode> = {
  DVLA:          <Bug  className="h-3.5 w-3.5 text-sev-critical" />,
  'ai-exploits': <Zap  className="h-3.5 w-3.5 text-sev-high" />,
  llm:           <Lock className="h-3.5 w-3.5 text-lime" />,
};

function sourceIcon(source: string) {
  if (source.toLowerCase().includes('dvla'))        return SOURCE_ICONS['DVLA'];
  if (source.toLowerCase().includes('ai-exploits')) return SOURCE_ICONS['ai-exploits'];
  return SOURCE_ICONS['llm'];
}

export default function LearnIndex() {
  const sorted = [...LEARN_ARTICLES].sort(
    (a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity]
  );

  const critical = sorted.filter(a => a.severity === 'critical');
  const highPlus  = sorted.filter(a => a.severity !== 'critical');

  return (
    <>
      <Helmet>
        <title>Learn Security Vulnerabilities — Real Code Examples | GitHubMate</title>
        <meta
          name="description"
          content="Free security vulnerability guide with real vulnerable code from DVLA, CVE-2024-1561, CVE-2024-2912 and more. Learn OWASP LLM Top 10 and OWASP Top 10:2021 with actual exploit patterns."
        />
        <meta
          name="keywords"
          content="OWASP LLM Top 10 examples, prompt injection tutorial, SQL injection LangChain, Gradio CVE-2024-1561, Ray RCE, BOLA IDOR tutorial, JWT alg none, GitHub Actions shell injection"
        />
        <link rel="canonical" href={`${SITE}/learn`} />
        <meta property="og:title" content="Learn Security Vulnerabilities — Real Code Examples" />
        <meta property="og:description" content="OWASP LLM Top 10 and OWASP Top 10:2021 with real vulnerable code from known-bad repos and public CVEs." />
        <meta property="og:url" content={`${SITE}/learn`} />
      </Helmet>

      <div className="min-h-screen bg-charcoal-bg">

        {/* ── Hero ─────────────────────────────────────────────────────────── */}
        <section className="max-w-5xl mx-auto px-4 pt-14 pb-12">
          <nav className="text-sm text-muted mb-8 flex items-center gap-1.5" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-gray-900 transition-colors">Home</Link>
            <ChevronRight className="h-3.5 w-3.5 text-faint" />
            <span className="text-gray-800">Learn Vulnerabilities</span>
          </nav>

          <div className="eyebrow mb-5">
            <BookOpen className="h-3.5 w-3.5" />
            Security Vulnerability Guide
          </div>

          <h1 className="font-display text-4xl md:text-5xl font-extrabold text-gray-900 leading-tight mb-4">
            Real Code.<br className="hidden md:block" />
            Real CVEs.<br className="hidden md:block" />
            <span className="text-lime">No Hand-Waving.</span>
          </h1>
          <p className="text-lg text-gray-700 max-w-2xl mb-8 leading-relaxed">
            Every article is grounded in a known-vulnerable codebase or published CVE — not invented examples.
            See the exact exploit pattern, understand why it works, and learn how GitHubMate detects it.
          </p>

          {/* Source attribution pills */}
          <div className="flex flex-wrap gap-2 mb-2">
            {[
              { label: 'DVLA — Damn Vulnerable LLM Agent', color: 'text-sev-critical' },
              { label: 'ProtectAI ai-exploits', color: 'text-sev-high' },
              { label: 'greshake llm-security', color: 'text-lime' },
              { label: 'CVE-2024-1561 · CVE-2024-2912', color: 'text-accent-teal' },
            ].map(({ label, color }) => (
              <span
                key={label}
                className="inline-flex items-center gap-1.5 bg-charcoal-card border border-charcoal-border rounded-full px-3 py-1 text-xs font-mono text-muted"
              >
                <span className={`w-1.5 h-1.5 rounded-full bg-current ${color}`} />
                {label}
              </span>
            ))}
          </div>
        </section>

        {/* ── Stats strip ──────────────────────────────────────────────────── */}
        <section className="border-y border-charcoal-border bg-charcoal-panel">
          <div className="max-w-5xl mx-auto px-4 py-6">
            <dl className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              {[
                { value: `${LEARN_ARTICLES.length}`, label: 'Deep-dive articles' },
                { value: `${LEARN_ARTICLES.filter(a => a.severity === 'critical').length}`, label: 'Critical severity' },
                { value: '3', label: 'Real vulnerable repos' },
                { value: '4+', label: 'Public CVEs referenced' },
              ].map(stat => (
                <div key={stat.label}>
                  <dt className="font-display text-3xl font-extrabold text-lime">{stat.value}</dt>
                  <dd className="text-xs text-muted mt-1 uppercase tracking-wider">{stat.label}</dd>
                </div>
              ))}
            </dl>
          </div>
        </section>

        {/* ── Critical severity articles ────────────────────────────────────── */}
        <section className="max-w-5xl mx-auto px-4 pt-12 pb-4">
          <h2 className="font-display text-xl font-bold text-gray-900 mb-1 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-sev-critical inline-block" />
            Critical Severity
          </h2>
          <p className="text-sm text-muted mb-6">Exploitation gives an attacker code execution, full data access, or account takeover.</p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {critical.map(article => (
              <ArticleCard key={article.slug} article={article} />
            ))}
          </div>
        </section>

        {/* ── High / Medium articles ────────────────────────────────────────── */}
        {highPlus.length > 0 && (
          <section className="max-w-5xl mx-auto px-4 pt-8 pb-12">
            <h2 className="font-display text-xl font-bold text-gray-900 mb-1 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-sev-high inline-block" />
              High Severity
            </h2>
            <p className="text-sm text-muted mb-6">Significant impact — data leakage, privilege escalation, or broken access control.</p>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {highPlus.map(article => (
                <ArticleCard key={article.slug} article={article} />
              ))}
            </div>
          </section>
        )}

        {/* ── CTA ──────────────────────────────────────────────────────────── */}
        <section className="border-t border-charcoal-border bg-charcoal-panel">
          <div className="max-w-5xl mx-auto px-4 py-14 text-center">
            <div className="eyebrow mb-5 mx-auto w-fit">
              <Shield className="h-3.5 w-3.5" />
              Free Scanner
            </div>
            <h2 className="font-display text-3xl font-extrabold text-gray-900 mb-3">
              Scan your repo for every pattern in this guide
            </h2>
            <p className="text-gray-700 mb-8 max-w-xl mx-auto">
              GitHubMate detects all vulnerabilities documented here — free, no signup, runs entirely in your browser in 30 seconds.
            </p>
            <Link to="/" className="btn-primary inline-flex items-center gap-2 text-base px-7 py-3">
              <Shield className="h-5 w-5" />
              Run a free scan
            </Link>
          </div>
        </section>

      </div>
    </>
  );
}

function ArticleCard({ article }: { article: (typeof LEARN_ARTICLES)[number] }) {
  const sourceName = article.realWorld.source.split(' — ')[0].split(' (')[0];

  return (
    <Link
      to={`/learn/${article.slug}`}
      className="card flex flex-col group hover:border-lime/50 hover:shadow-[0_0_30px_rgba(159,232,112,0.10)]"
    >
      {/* Top row: severity + OWASP tag */}
      <div className="flex items-center justify-between mb-4">
        <span className={SEV_CLASS[article.severity]}>{article.severity}</span>
        <span className="font-mono text-xs text-faint">
          {article.owasp.split(':')[0]}
        </span>
      </div>

      {/* Title */}
      <h3 className="font-display font-bold text-gray-900 text-base leading-snug mb-2 group-hover:text-lime transition-colors">
        {article.title}
      </h3>

      {/* Subtitle */}
      <p className="text-sm text-muted leading-relaxed line-clamp-2 mb-4 flex-1">
        {article.subtitle}
      </p>

      {/* Tags */}
      <div className="flex flex-wrap gap-1.5 mb-4">
        {article.tags.slice(0, 3).map(tag => (
          <span
            key={tag}
            className="text-xs font-mono bg-charcoal-bg border border-charcoal-border text-faint px-2 py-0.5 rounded"
          >
            {tag}
          </span>
        ))}
      </div>

      {/* Source */}
      <div className="flex items-center justify-between pt-3 border-t border-charcoal-border">
        <div className="flex items-center gap-1.5">
          {sourceIcon(article.realWorld.source)}
          <span className="text-xs text-faint font-mono truncate max-w-[160px]">{sourceName}</span>
        </div>
        <ChevronRight className="h-4 w-4 text-faint group-hover:text-lime transition-colors flex-shrink-0" />
      </div>
    </Link>
  );
}
